<template>
	<button
		class="btn btn-ghost btn-sm btn-dev whitespace-nowrap"
		:disabled="busy || !!status?.cancelRequested"
		:title="title"
		@click="onClick">
		<template v-if="running">
			<span class="loading loading-spinner loading-xs"></span>
			{{ status!.done + status!.failed }} / {{ status!.total }}
			<span v-if="status!.failed" class="text-error">({{ status!.failed }})</span>
			<Square class="h-3 w-3" />
		</template>
		<template v-else>
			<RefreshCw class="h-4 w-4" />
			{{ _tr(tk('start')) }}
			<span v-if="lastFailed" class="badge badge-error badge-xs">{{ lastFailed }}</span>
		</template>
	</button>
</template>

<script setup lang="ts">
	/**
	 * Developer button for the server-side recalc-all run — the slim variant of
	 * RecalcModulesCard for page headers (hubs, custom-graph list). Starts the
	 * run; while it goes it shows the progress and a click cancels after the
	 * current item. Per-row states show in the tables, the full card on the
	 * admin page.
	 */
	import { computed } from 'vue'
	import dayjs from 'dayjs'
	import { RefreshCw, Square } from 'lucide-vue-next'
	import { useI18n } from 'vue-i18n'
	import { _translations } from './_translations'
	import { useRecalcAll, type RecalcKind } from './useRecalcAll'

	const props = withDefaults(defineProps<{ kind?: RecalcKind; count?: number; ids?: string[] }>(), {
		kind: 'modules',
		count: 0,
		ids: undefined,
	})

	const { t: _tr } = useI18n(_translations)
	const { status, busy, start, cancel } = useRecalcAll(props.kind)

	const tk = (key: string) => (props.kind === 'graphs' ? `admin.recalcAll.graphs.${key}` : `admin.recalcAll.${key}`)

	const running = computed(() => !!status.value?.running)
	const lastFailed = computed(() => (status.value?.startedAt ? status.value.failed : 0))

	const title = computed(() => {
		const st = status.value
		if (st?.cancelRequested) return _tr(tk('cancelling'))
		if (st?.running) return _tr('admin.recalcAll.button.cancelHint')
		if (!st?.startedAt) return _tr('admin.recalcAll.never')
		return _tr('admin.recalcAll.button.last', {
			date: dayjs(st.finishedAt || st.startedAt).format('DD.MM.YYYY HH:mm'),
			done: st.done,
			failed: st.failed,
		})
	})

	const onClick = async () => {
		if (running.value) {
			if (window.confirm(_tr('admin.recalcAll.button.cancelConfirm'))) await cancel()
			return
		}
		const msg = props.count ? _tr(tk('confirm'), { count: props.count }) : _tr(tk('confirmUnknown'))
		if (!window.confirm(msg)) return
		await start(props.ids?.length ? { ids: props.ids } : {})
	}
</script>
